import React from "react";
import { ArrowRight, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../ui/dialog";
import { useTranslation } from "../../../hooks/useTranslation";
import { useTenant } from "../../../contexts/TenantContext";
import { useBillingAccount, useBillingCatalog, useBillingUsage } from "../../../hooks/useApi";
import { BrandStyles, TESTO_AZIONE, VARIABILI_SEZIONE } from "../../integrations/brand";
import { GRIGIO, SCURO } from "../../integrations/casella";
import { CONSIGLIATO, GRATUITO, selfService } from "../../billing/piani";
import { SchedaPiano } from "../../billing/SchedaPiano";

/**
 * I piani da proporre a chi supera le fonti nell'onboarding: quelli che si
 * comprano online, dal piu' economico. Solo a chi puo' pagare (organizzazione
 * principale, titolare), sul piano gratuito, con i pagamenti online attivi;
 * non a chi e' stato venduto offline. Se i dati non arrivano, null: resta
 * l'avviso scritto di sempre.
 */
export function usePianiDaProporre() {
  const { canManageBilling } = useTenant();
  const consumi = useBillingUsage();
  const catalogo = useBillingCatalog();
  const conto = useBillingAccount({ enabled: Boolean(canManageBilling) });

  const pagamenti = conto.data?.pagamenti;
  if (!canManageBilling || consumi.data?.plan?.key !== GRATUITO) return null;
  if (!pagamenti?.stripe_attivo || pagamenti.puo_gestire === false || pagamenti.vendita_offline) return null;
  const piani = (catalogo.data?.plans || [])
    .filter((p) => p.key !== GRATUITO && selfService(p.key) && (p.price_yearly > 0 || p.price_monthly > 0))
    .sort((a, b) => (a.price_yearly || a.price_monthly * 12) - (b.price_yearly || b.price_monthly * 12));
  return piani.length ? { piani, limiti: consumi.data?.effective?.limits || {} } : null;
}

/**
 * Le fonti non stanno nel piano gratuito: si apre subito la scelta del piano,
 * con le stesse schede della pagina Abbonamento (un solo bottone scuro, quello
 * del piano consigliato). Il gratuito non ha una scheda: e' il bottone grigio
 * sotto, che chiude e lascia continuare l'onboarding. Il pagamento parte dalla
 * pagina Abbonamento, che chiede prima i dati di fatturazione.
 */
export default function ModalePiani({ proposta, motivo, onScegli, onGratuito }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(aperto) => (aperto ? null : onGratuito())}>
      <DialogContent
        className="max-h-[92vh] max-w-[1000px] overflow-y-auto sm:rounded-2xl"
        // Sfondo leggero: la pagina dietro si intravede (richiesta dopo la live).
        overlayClassName="bg-slate-900/25"
        style={VARIABILI_SEZIONE}
      >
        <BrandStyles />
        <div className="text-center">
          <DialogTitle className="text-2xl font-semibold leading-8 text-[#020617]">
            {t("onboardingWizard.plansOverTitle")}
          </DialogTitle>
          <DialogDescription className="mx-auto mt-2 max-w-[640px] text-sm leading-6 text-[#64748B]">
            {motivo || t("onboardingWizard.plansOverText", { limit: proposta.limiti.sources_max })}
          </DialogDescription>
        </div>
        <div className="mt-4 grid gap-5 md:grid-cols-3">
          {proposta.piani.map((piano) => (
            <SchedaPiano
              key={piano.key}
              piano={piano}
              intervallo="yearly"
              inEvidenza={piano.key === CONSIGLIATO}
              azione={
                <button
                  type="button"
                  className={`${piano.key === CONSIGLIATO ? SCURO : GRIGIO} w-full`}
                  onClick={() => onScegli(`/billing?piano=${piano.key}`)}
                >
                  {t("onboardingWizard.planCta", { plan: piano.name })}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              }
            />
          ))}
        </div>
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[#F1F5F9] px-4 text-sm font-semibold text-[#18181B] transition-colors hover:bg-[#E2E8F0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onGratuito}
          >
            {t("onboardingWizard.continueFree")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs leading-5 text-[#64748B]">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{t("onboardingWizard.plansTrust")}</span>
          <button type="button" className={`font-semibold ${TESTO_AZIONE}`} onClick={() => onScegli("/billing")}>
            {t("onboardingWizard.plansCompare")}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
